import React, { useState, useEffect, useCallback } from 'react';
import { ConsoleShell, useConsoleTheme } from '../components/Console';
import { Card, CardTitle, Insight, Btn } from '../components/UI';
import { supabase } from '../supabaseClient';
import { getDisplayAddress } from '../utils/location';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const today = new Date().toISOString().slice(0,10);

function useIsMobile() {
  const [mob, setMob] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => { const h = () => setMob(window.innerWidth < 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  return mob;
}

function VerifyQueueBody() {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const isMobile = useIsMobile();
  const [country, setCountry]   = useState('');
  const [type_,   setType_]     = useState('');
  const [rows,    setRows]      = useState([]);
  const [loading, setLoading]   = useState(false);
  const [saving,  setSaving]    = useState(null);
  const [stats,   setStats]     = useState(null);
  const [done,    setDone]      = useState(0);

  const fetchStats = useCallback(async () => {
    try {
      const { count: total } = await supabase.from('Locations').select('*', { count: 'exact', head: true });
      const { count: never  } = await supabase.from('Locations').select('*', { count: 'exact', head: true }).is('last_verified', null);
      const { count: stale  } = await supabase.from('Locations').select('*', { count: 'exact', head: true }).lt('last_verified', (new Date(Date.now()-365*24*60*60*1000)).toISOString().slice(0,10));
      setStats({ total: total||0, never: never||0, stale: stale||0 });
    } catch (_e) {}
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const loadBatch = useCallback(async () => {
    setLoading(true);
    let q = supabase.from('Locations').select('id,name,type,city,country,address,formatted_address,phone,website,last_verified,tags').is('last_verified', null).limit(10);
    if (country) q = q.eq('country', country);
    if (type_)   q = q.ilike('type', type_);
    const { data } = await q;
    setRows(data || []);
    setLoading(false);
  }, [country, type_]);

  async function markVerified(id) {
    setSaving(id);
    await supabase.from('Locations').update({ last_verified: today }).eq('id', id);
    setRows(prev => prev.filter(r => r.id !== id));
    setDone(d => d+1);
    setSaving(null);
    if (rows.length <= 2) loadBatch();
  }

  async function flagIssue(id) {
    setSaving(id);
    await supabase.from('Locations').update({ notes: '[NEEDS REVIEW]', last_verified: today }).eq('id', id);
    setRows(prev => prev.filter(r => r.id !== id));
    setDone(d => d+1);
    setSaving(null);
    if (rows.length <= 2) loadBatch();
  }

  const skip = (id) => setRows(prev => [...prev.filter(r => r.id !== id), prev.find(r => r.id === id)]);

  return (
    <div style={{ fontFamily: F, padding: isMobile ? '16px 14px 80px' : '24px 28px' }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 500, margin: 0, color: 'var(--color-text-primary)' }}>Verification queue</h2>
        <p style={{ fontSize: '.78rem', color: 'var(--color-text-secondary)', margin: '4px 0 0' }}>
          Review locations that have never been verified. Mark as verified, flag issues, or skip.
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px,1fr))', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'Total locations', val: stats.total.toLocaleString(), color: '#314F5C' },
            { label: 'Never verified', val: stats.never.toLocaleString(), color: '#dc3545' },
            { label: 'Stale (>1yr)',   val: stats.stale.toLocaleString(), color: '#fd7e14' },
            { label: 'Done this session', val: done.toString(), color: '#28a745' },
          ].map(({ label, val, color }) => (
            <Card key={label} style={{ padding: '12px 14px' }}>
              <div style={{ fontSize: '.65rem', color: 'var(--color-text-secondary)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 500, color }}>{val}</div>
            </Card>
          ))}
        </div>
      )}

      {/* Filters + load */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input value={country} onChange={e => setCountry(e.target.value)} placeholder="Country (e.g. Cambodia)"
          style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)', color: 'var(--color-text-primary)', fontSize: '.78rem', fontFamily: F, width: 180 }} />
        <input value={type_} onChange={e => setType_(e.target.value)} placeholder="Type (e.g. Hospital)"
          style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)', color: 'var(--color-text-primary)', fontSize: '.78rem', fontFamily: F, width: 160 }} />
        <button onClick={loadBatch} disabled={loading} style={{ padding: '7px 18px', borderRadius: 8, background: t.accent, color: '#AFE1F6', border: 'none', fontSize: '.78rem', fontWeight: 500, cursor: loading ? 'default' : 'pointer', fontFamily: F }}>
          {loading ? 'Loading…' : rows.length ? 'Load more' : 'Load batch (10)'}
        </button>
      </div>

      {/* Queue */}
      {rows.length === 0 && !loading && (
        <Insight type="info">Click "Load batch" to start verifying locations.</Insight>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map(row => (
          <Card key={row.id} style={{ padding: '14px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '.85rem', fontWeight: 500, color: 'var(--color-text-primary)', marginBottom: 3 }}>{row.name}</div>
                <div style={{ fontSize: '.72rem', color: 'var(--color-text-secondary)' }}>
                  {[row.type, row.city, row.country].filter(Boolean).join(' · ')}
                </div>
                {getDisplayAddress(row) && <div style={{ fontSize: '.7rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>{getDisplayAddress(row)}</div>}
                {row.phone   && <div style={{ fontSize: '.7rem', color: '#6F8FA7', marginTop: 2 }}>{row.phone}</div>}
                {row.website && <a href={row.website} target="_blank" rel="noreferrer" style={{ fontSize: '.7rem', color: '#6F8FA7' }}>{row.website.replace(/^https?:\/\//, '')}</a>}
                {row.tags    && <div style={{ fontSize: '.65rem', color: 'var(--color-text-secondary)', marginTop: 3, fontStyle: 'italic' }}>{row.tags}</div>}
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button onClick={() => markVerified(row.id)} disabled={saving === row.id}
                  style={{ padding: '5px 12px', borderRadius: 6, background: '#28a745', color: '#fff', border: 'none', fontSize: '.72rem', cursor: 'pointer', fontFamily: F, fontWeight: 500 }}>
                  {saving === row.id ? '…' : '✓ Verified'}
                </button>
                <button onClick={() => flagIssue(row.id)} disabled={saving === row.id}
                  style={{ padding: '5px 12px', borderRadius: 6, background: '#fd7e14', color: '#fff', border: 'none', fontSize: '.72rem', cursor: 'pointer', fontFamily: F }}>
                  Flag
                </button>
                <button onClick={() => skip(row.id)}
                  style={{ padding: '5px 12px', borderRadius: 6, background: 'none', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border-secondary)', fontSize: '.72rem', cursor: 'pointer', fontFamily: F }}>
                  Skip
                </button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function VerifyQueue(props) {
  if (props && props.embed) return <VerifyQueueBody {...props} />;
  return <ConsoleShell title="Verify Queue"><VerifyQueueBody {...props} /></ConsoleShell>;
}
